import { useCallback, useRef } from 'react';
import {
  useRoute,
  useNavigation,
  useFocusEffect,
} from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { filterAdded, paramsReseted, selectParams } from '@/store/params';
import useListQueries from '@/hooks/useListQueries';

// ── Preserve-intent flags (module-level) ────────────────────────────────────
// `openChild` sets the flag before navigating to a screen's own detail/form;
// the focus effect consumes it on return to PRESERVE params (refetch in place)
// vs RESET them (navigated here from elsewhere). Kept here since useListScreen
// is the only consumer.
const preserveFlags = {};
const setPreserveIntent = (scope) => {
  preserveFlags[scope] = true;
};
const consumePreserveIntent = (scope) => {
  const had = !!preserveFlags[scope];
  delete preserveFlags[scope];
  return had;
};

// Shared list-screen orchestrator. SINGLE-SOURCES the scope: it derives `scope`
// once, selects the scoped `params`, builds the query arg, runs the resource's RTK
// Query hooks (via useListQueries), owns the focus → preserve/reset → refetch
// lifecycle, and vends openChild. The scope used for the queries and for the
// lifecycle dispatches is guaranteed identical (no screen/hook desync).
//
//   const { list, count, openChild, scope, params } = useListScreen({
//     listHook, countHook, infiniteHook,
//     toFilter,   // optional: (rawFilter) => apiFilter; omit when used as-is
//     scopeKey,   // optional: defaults to the route name (set only for >1 list/screen)
//     presetFilter, // optional: a raw-filter VALUE to seed once (cross-screen carry-over)
//   });
//
// The screen reads back `scope` (for filterName props) and `params` (for search
// value / filter draft). It supplies only what's genuinely its own: the resource
// hooks and the filter transform.
//
// Cross-screen seeding: the screen decides WHICH nav param carries the seed and
// passes its VALUE as `presetFilter` (e.g. `presetFilter: route.params?.myKey`).
// This hook owns only the tricky part — applying it once, without an unfiltered
// mount request and without a follow-up reset. No route-param key is hardcoded
// here, so different callers can use whatever param name they like.
export default function useListScreen({
  listHook,
  countHook,
  infiniteHook,
  toFilter,
  scopeKey,
  presetFilter,
} = {}) {
  const route = useRoute();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const scope = scopeKey || route.name;
  const params = useSelector(selectParams(scope));

  // Cross-screen filter seed (e.g. a dashboard card → this list), passed by the screen
  // as a value. Applied once per distinct preset (compared by reference — a new
  // navigation makes a new object). We remember what we've applied rather than clearing
  // any route param, so applying the seed can't re-trigger the focus effect and reset it.
  const appliedPresetRef = useRef(null);
  const preset = presetFilter;
  const presetPending = !!preset && preset !== appliedPresetRef.current;

  const arg = {
    filter: toFilter ? toFilter(params.filter) : params.filter,
    skip: params.skip,
    limit: params.limit,
    sort: params.sort,
  };

  // While a preset is pending, skip the query so it doesn't fire an unfiltered
  // request on mount — it runs once, with the filter, right after the seed lands.
  const { list, count, listQuery, infiniteQuery } = useListQueries({
    listHook,
    countHook,
    infiniteHook,
    arg,
    params,
    skip: presetPending,
  });

  // refetch is rebuilt every render (list/count are fresh RTK result objects); keep
  // it in a ref so the focus-effect callback stays stable — otherwise useFocusEffect
  // re-runs every render and the paramsReseted dispatch loops.
  const refetchRef = useRef();
  refetchRef.current = () => {
    list?.refetch?.();
    count?.refetch?.();
  };

  // The query already fetches on mount; the FIRST focus must not also reset+refetch
  // (paramsReseted bumps refreshTick → a redundant second request). Only run the
  // preserve/reset lifecycle on subsequent focuses (returning to the screen).
  const firstFocusRef = useRef(true);

  useFocusEffect(
    useCallback(() => {
      const isFirstFocus = firstFocusRef.current;
      firstFocusRef.current = false;

      // (1) cross-screen carry-over: apply each distinct preset once. Mark it applied
      // (by reference) so the follow-up render doesn't re-apply or reset it.
      if (preset && preset !== appliedPresetRef.current) {
        appliedPresetRef.current = preset;
        dispatch(filterAdded({ key: scope, filter: preset }));
        return; // arg changed + skip lifts → the query fetches once, with the filter
      }
      // Initial mount — the query's own fetch handles the first load.
      if (isFirstFocus) {
        return;
      }
      // (2) preserve (came back from own detail/form via openChild) vs reset.
      if (consumePreserveIntent(scope)) {
        refetchRef.current?.();
      } else {
        dispatch(paramsReseted({ key: scope }));
      }
    }, [scope, preset, dispatch]),
  );

  const openChild = useCallback(
    (routeName, navParams) => {
      setPreserveIntent(scope);
      navigation.navigate(routeName, navParams);
    },
    [scope, navigation],
  );

  return { list, count, openChild, scope, params, listQuery, infiniteQuery };
}
